import { useRef, useEffect, useState, type ReactNode } from "react";
import Highcharts, { type SeriesLegendItemClickEventObject, type YAxisOptions } from "highcharts";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import "highcharts/modules/accessibility";
import AnnotationLabel, { type Annotation } from "./AnnotationLabel";
import { trellisPalette } from "components/custom/analytics/palettes";
import { useFetch } from "hooks/api";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import moment, { type unitOfTime } from "moment";
import { useLegend } from "../../../legend/useLegend";
import { formatNumber } from "utils/formatNumber";
import logo from "assets/images/logo/Trellis_Logomark_color.png";
import { useDates } from "dates/DatesContext";
import { useMediaQuery } from "usehooks-ts";
import { useAppSelector } from "../../../redux/hooks";
import { css } from "../../../../styled-system/css";

declare module "highcharts" {
  // in TS these are normally only available for specific chart types, but at runtime specifying it for all series works
  interface PlotSeriesOptions {
    pointPadding?: number;
    groupPadding?: number;
  }

  interface XAxisLabelsOptions {
    customFormatter?: (value: string | number) => string;
  }

  interface Chart {
    isDirtyLegend?: boolean;
  }

  interface LegendItemObject {
    textStr?: string;
  }

  interface Legend {
    render: () => void;
  }

  interface SeriesEventsOptionsObject {
    render?: () => void;
  }
}

const labelContainerClasses = css({
  display: "flex",
  position: "relative",
  justifyContent: "space-around",
  height: "10px",
  marginLeft: "52px",
  marginRight: "52px",
  marginTop: "-50px",
  marginBottom: "50px",
});

interface Props {
  context?: "multi-metric";
  group?: string;
  handleLegendChange?: (e: SeriesLegendItemClickEventObject) => boolean;
  height?: string | number | null;
  isChartDataLoading?: boolean;
  options: Highcharts.Options;
  selectedAd?: string | number | null;
  selectedCategory?: string | number | null;
  selectedProduct?: string | number | null;
  showAnnotations?: boolean;
  yLabels?: boolean;
}

const AnnotationChartTemplate = ({
  options,
  group,
  selectedProduct,
  selectedCategory,
  selectedAd,
  height,
  yLabels,
  context,
  showAnnotations,
  isChartDataLoading = false,
  handleLegendChange,
}: Props) => {
  const mediaQuery = useMediaQuery("(max-width:600px)");
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [annotationData, setAnnotationData] = useState<{ date: string; annotations: Annotation[] }[]>([]);

  // By default, annotations assumed depending on subscription tier, unless explicity disabled in showAnnotations prop (i.e. the organization summary chart)
  const annotationsEnabled = showAnnotations !== false;

  const [legendPreferences, setLegend] = useLegend(context ?? "other");
  const { group: contextGroup } = useDates();
  const chartRef = useRef<HighchartsReactRefObject>(null);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const resolvedGroup = contextGroup ?? group;

  useEffect(() => {
    // Set selected legend items based on status passed down from legend context
    if (chartRef.current?.chart.series) {
      chartRef.current.chart.series.forEach((s) => {
        s.visible = legendPreferences?.find((legendItem) => legendItem.name === s.name)?.visible ?? false;
      });

      chartRef.current.chart.legend.allItems.forEach((i) => {
        if (i instanceof Highcharts.Series) {
          i.userOptions.visible = legendPreferences?.find((legendItem) => legendItem.name === i.name)?.visible ?? false;
        }
      });
    }

    chartRef.current?.chart && chartRef.current.chart.redraw();
  }, [legendPreferences]);

  let { series = [], yAxis, xAxis, title = { text: "" }, plotOptions } = options;
  yAxis = yAxis as Highcharts.YAxisOptions | undefined;
  xAxis = xAxis as Highcharts.XAxisOptions | undefined;

  const marketplace = marketPlace.marketPlace;

  const baseQuery: {
    marketplace: string;
    category?: string | number | null;
    product?: string | number | null;
    advertisement?: string | number | null;
    force_start_dates?: boolean;
  } = { marketplace };

  const ts_after =
    contextGroup === "month" ?
      `${moment(xAxis?.categories?.[0]).startOf("M").format("YYYY-MM-DD")}T00:00:00`
    : `${moment(xAxis?.categories?.[0]).format("YYYY-MM-DD")}T00:00:00`;
  const ts_before =
    contextGroup === "month" ?
      `${moment(xAxis?.categories?.at(-1)).endOf("M").format("YYYY-MM-DD")}T23:59:59`
    : `${moment(xAxis?.categories?.at(-1)).format("YYYY-MM-DD")}T23:59:59`;

  if (selectedCategory !== "all") {
    baseQuery["category"] = selectedCategory;
  }

  if (selectedProduct) {
    baseQuery["product"] = selectedProduct;
  }

  if (selectedAd) {
    baseQuery["advertisement"] = selectedAd;
  }

  const annotationQuery = useFetch<{ date: string; annotations: Annotation[] }[]>(
    ["annotations"],
    "/api/annotations/",
    {
      groupby: resolvedGroup,
      ts_after,
      ts_before,
      ...baseQuery,
    },
    // ts_before and ts_after need values from categories to contain start and end date within query string
    {
      enabled: !!xAxis?.categories?.[0],
      placeholderData: keepPreviousData,
      staleTime: 2 * 60 * 1000,
      select: (d) => d.data,
    },
  );

  useEffect(() => {
    setSelectedDate(null);
  }, [selectedProduct, selectedAd]);

  useEffect(() => {
    if (!annotationsEnabled) {
      setAnnotationData([]);
    }
  }, [annotationsEnabled]);

  useEffect(() => {
    if (annotationQuery.data && annotationsEnabled) {
      setAnnotationData(
        annotationQuery.data.filter((annotation) =>
          xAxis?.categories?.find((c) => moment(c).isSame(moment(annotation.date), contextGroup as unitOfTime.StartOf)),
        ),
      );
    }

    // Clear annotations if chart data is empty - no rollup stats to display
    if (!annotationQuery.data && annotationData.length > 0) {
      setAnnotationData([]);
    }
  }, [
    annotationData.length,
    annotationQuery.data,
    annotationQuery.isLoading,
    annotationsEnabled,
    context,
    contextGroup,
    xAxis?.categories,
  ]);

  const renderLabels = () => {
    const customLabels: ReactNode[] = [];

    if (chartRef.current?.chart && chartRef.current.chart.xAxis[0]) {
      // Add custom label associated with each tick on xAxis (date)
      Highcharts.objectEach(chartRef.current.chart.axes[0]?.ticks, (tick, index) => {
        if (annotationData[tick.pos]) {
          customLabels.push(
            <AnnotationLabel
              data={annotationData[tick.pos]}
              date={annotationData[tick.pos]!.date}
              selectedProduct={selectedProduct}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              addAnnotation={addAnnotation}
              editAnnotation={editAnnotation}
              removeAnnotation={removeAnnotation}
              key={index}
            />,
          );
        }
      });
    }
    return customLabels;
  };

  const chartOptions: Highcharts.Options = {
    title,
    chart: {
      marginLeft: context === "multi-metric" ? 15 : 50,
      marginRight: context === "multi-metric" ? 15 : 50,
      style: {
        fontFamily: "Poppins",
        fontSize: "15px",
        cursor: "zoom-in",
      },
      height: height,
    },
    plotOptions:
      plotOptions ? plotOptions : (
        ({
          series: {
            pointPadding: 0,
            groupPadding: 0.1,
            borderWidth: 0,
            shadow: false,
            events: {
              legendItemClick: (e) => {
                if (handleLegendChange) {
                  return handleLegendChange(e);
                }
                const legendOptions =
                  chartRef.current?.chart.series.map((s) => ({
                    name: s.name,
                    visible: s.visible,
                  })) ?? [];

                chartRef.current!.chart.isDirtyLegend = true;

                const item = legendOptions.find((item) => item.name === e.target.legendItem?.textStr)!;
                item.visible = !item.visible;

                setLegend(legendOptions);
                chartRef.current?.chart.redraw();
              },
              render: () => {
                if (chartRef.current && !chartOptions.legend!.enabled === false) {
                  chartRef.current.chart.legend.render();
                }
              },
            },
          },
        } satisfies Highcharts.PlotOptions)
      ),
    tooltip: {
      shared: true,
      formatter: function (tooltip) {
        let hasFormatter = false;
        const points = this.points ?? [];
        let markup = "";
        if (options.tooltip?.formatter) {
          hasFormatter = true;
          markup = options.tooltip.formatter.bind(this)(tooltip)?.toString() ?? "";
        }
        if (!hasFormatter) {
          markup =
            points.length ?
              '<span style="font-size: 10px; font-weight: bold; margin-left: 1rem">' + points[0]!.key + "</span><br/>"
            : "";
        }

        for (const point of points) {
          if (point.series.name !== "annotationAxis") {
            if (!hasFormatter) {
              markup +=
                `<span style="color:${typeof point.series.color === "string" ? point.series.color : "#000"}">\u25CF</span> ` +
                point.series.name +
                ": " +
                ((
                  point.series.name.includes("Sales") ||
                  point.series.name.includes("Spend") ||
                  point.series.name.includes("Product Price")
                ) ?
                  formatNumber(point.y, {
                    currency: { marketPlace: marketPlace.marketPlace },
                  })
                : formatNumber(
                    point.series.name.includes("Margin") ? +((point.y ?? 0) * 100).toFixed(2)
                    : point.series.name.includes("Units Goal") || point.series.name.includes("Ratings Goal") ?
                      Math.round(point.y ?? 0)
                    : point.y,
                  )) +
                (point.series.name.includes("ACOS") || point.series.name.includes("Margin") ? "%" : "") +
                "<br/>";
            }
          } else {
            markup +=
              `<span style="color:${typeof point.series.color === "string" ? point.series.color : "#000"}">\u25CF</span> # of Annotations: ` +
              annotationData.find((a) =>
                moment(a.date).isSame(moment(points[0]!.key), contextGroup as unitOfTime.StartOf),
              )?.annotations.length +
              "<br/>";

            // Show count of annotations and guide user to click on dots to view and interact with them
            markup += "<br/><span>Click on the pink dot<br/>below to view or create<br/>annotated events</span>";
          }
        }

        return markup;
      },
    },
    xAxis: [
      {
        crosshair: true,
        categories: xAxis?.categories ?? [],
        plotLines: xAxis?.plotLines ?? [],
        labels: {
          step:
            mediaQuery ? 0
            : contextGroup === "day" ?
              context === "multi-metric" ?
                (xAxis?.categories?.length ?? 0) > 90 ? 0
                : (xAxis?.categories?.length ?? 0) > 60 ? 10
                : (xAxis?.categories?.length ?? 0) > 30 ? 5
                : 2
              : 9
            : contextGroup === "week" && context === "multi-metric" ? 2
            : 1,
          formatter: function () {
            // Below allows custom label formtter where xAxis labels can be displayed differently that series category
            // Note: categories need to be synced between series and annotation axis
            if (xAxis?.labels?.customFormatter) {
              return xAxis.labels.customFormatter(this.value);
            }

            return this.value.toString();
          },
        },
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 1400,
          },
          chartOptions: {
            xAxis: {
              labels: {
                step:
                  contextGroup === "day" ?
                    context === "multi-metric" ?
                      2
                    : 11
                  : 1,
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 750,
          },
          chartOptions: {
            xAxis: {
              labels: {
                step: contextGroup === "day" ? 14 : 2,
              },
            },
          },
        },
      ],
    },
    yAxis: Array.from(Array(50)).map(
      (): YAxisOptions => ({
        title: yAxis?.title ?? { text: "" },
        min: 0,
        labels: {
          enabled: yLabels,
        },
      }),
    ),
    legend: {
      itemStyle: {
        fontWeight: "bold",
      },
      itemHiddenStyle: {
        "text-decoration": "none",
        color: "#bbb",
      },
      ...(options.legend ?
        options.legend
      : {
          verticalAlign: "top",
        }),
    },
    series: [
      {
        name: "annotationAxis",
        id: "annotationAxis",
        type: "line",
        lineWidth: 0,
        states: { hover: { lineWidthPlus: 0 } },
        showInLegend: false,
        data: annotationData.map((a) => {
          const shouldShow =
            (contextGroup === "day" &&
              (selectedProduct || selectedAd || selectedCategory) &&
              (selectedCategory !== "all" || selectedProduct)) ||
            a.annotations.length > 0;

          return shouldShow ?
              {
                y: 0,
                className: css({
                  cursor: "pointer",
                }),
                color: a.annotations.length > 0 || a.date === selectedDate ? trellisPalette[1] : "none",
                marker: {
                  states: {
                    hover: {
                      fillColor: trellisPalette[1],
                    },
                  },
                },
              }
            : null;
        }),
        events: {
          click: (e) => {
            e.preventDefault();

            if (
              contextGroup === "day" ||
              (annotationData.find((a) => a.date === moment(e.point.category).format("YYYY-MM-DD"))?.annotations
                .length ?? 0) > 0
            ) {
              if (moment(e.point.category).format("YYYY-MM-DD") === selectedDate) {
                setSelectedDate(null);
              } else {
                setSelectedDate(moment(e.point.category).format("YYYY-MM-DD"));
              }
            }
          },
        },
        cursor: "pointer",
        yAxis: 1,
        step: "right",
        visible: true,
        color: trellisPalette[0],
        zIndex: 9999999,
        marker: {
          enabled: true,
          symbol: "circle",
          radius: 6,
        },
      },
      ...series,
    ],
  };

  // Annotation CRUD Requests
  const queryClient = useQueryClient();

  const createAnnotation = useMutation({
    mutationFn: async (newAnnotation: {
      category?: string | number | null;
      marketplace: string;
      description: string;
      ts: string;
      product?: string | number | null;
      advertisement?: string | number | null;
    }) => {
      return await api.post("/api/annotations/", newAnnotation);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["annotations"],
      });
    },
  });

  const updateAnnotation = useMutation({
    mutationFn: async (updatedAnnotation: {
      id: number;
      marketplace: string;
      description: string | null;
      ts: string;
    }) => {
      const { id, marketplace, description, ts } = updatedAnnotation;
      return await api.put(`/api/annotations/${id}/`, {
        category: selectedCategory,
        marketplace,
        description,
        ts,
      });
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["annotations"],
      });
    },
  });

  const deleteAnnotation = useMutation({
    mutationFn: async (id: number) => {
      return await api.remove(`/api/annotations/${id}/`);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["annotations"],
      });
    },
  });

  const addAnnotation = (formData: { annotation: string }) => {
    const { annotation } = formData;

    const newAnnotationData: {
      category?: string | number | null;
      marketplace: string;
      description: string;
      ts: string;
      product?: string | number | null;
      advertisement?: string | number | null;
    } = {
      marketplace: marketPlace.marketPlace,
      description: annotation,
      ts: `${selectedDate}T00:00:00Z`,
    };

    if (selectedCategory && selectedCategory !== "all") {
      newAnnotationData["category"] = selectedCategory;
    }

    if (selectedProduct) {
      newAnnotationData["product"] = selectedProduct;
    }

    if (selectedAd) {
      newAnnotationData["advertisement"] = selectedAd;
    }
    createAnnotation.mutate(newAnnotationData);
  };

  const editAnnotation = (currentAnnotation: {
    id: number;
    marketplace: string;
    description: string | null;
    ts: string;
  }) => {
    updateAnnotation.mutate(currentAnnotation);
  };

  const removeAnnotation = (id: number) => {
    deleteAnnotation.mutate(id);
  };

  return (
    <div className={css({ position: "relative", width: "100%" })}>
      {isChartDataLoading ?
        <div
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "430px",
          })}
        >
          <img src={logo} alt="Loading..." className={`rotate`} width="80" />
        </div>
      : <>
          <HighchartsReact
            highcharts={Highcharts}
            options={{ ...chartOptions }}
            ref={chartRef}
            callback={(chart: unknown) => {
              if (
                typeof chart === "object" &&
                chart &&
                "getMargins" in chart &&
                typeof chart.getMargins === "function"
              ) {
                chart.getMargins();
              }
            }}
            oneToOne={true}
            allowChartUpdate={true}
          />
          {annotationsEnabled && <div className={labelContainerClasses}>{renderLabels()}</div>}
        </>
      }
    </div>
  );
};

export default AnnotationChartTemplate;
